import React, {
  ReactNode,
  useContext,
  useState,
  // PropsWithChildren,
  useEffect,
  useRef,
} from "react"

import { spacing, IconCrossMedium } from "@izettle/otto"

import { IconButton, HeaderContent, Alignments } from "@/core/components/shared"

import {
  ModalHeader,
  ModalContent,
  Background,
  ModalWrapper,
  ModalContainer,
} from "./styles"

export enum ModalType {
  Clip = "Clip",
  Full = "Full",
}

const ModalContext = React.createContext({
  shownModalId: null as string | null,
  setShownModalId: (_id: string | null) => {},
  originBtnRef: null as React.RefObject<HTMLButtonElement> | null,
  setOriginBtnRef: (_ref: React.RefObject<HTMLButtonElement> | null) => {},
  hideModal: () => {},
})

export type ModalProps = {
  id: string
  title: string
  type: ModalType
}

export const Modal: React.FC<ModalProps> = ({ id, children, title, type }) => {
  const { shownModalId, hideModal } = useContext(ModalContext)
  const closeBtnRef = useRef<HTMLButtonElement>(null)

  const hide = () => {
    hideModal()
  }

  const full = type === ModalType.Full

  useEffect(() => {
    if (id === shownModalId) {
      closeBtnRef?.current?.focus()
    }
  }, [shownModalId, closeBtnRef])

  return (
    <ModalContainer
      role="dialog"
      full={full}
      id={id}
      className={id === shownModalId ? "show" : undefined}
    >
      <ModalWrapper className={!full ? "small" : undefined}>
        <ModalHeader>
          <IconButton
            ref={closeBtnRef}
            data-testid="close-modal"
            html={IconCrossMedium.svg}
            margin={spacing.medium}
            style={{ position: "absolute", right: 0 }}
            onClick={hide}
          />
          <HeaderContent alignment={full ? Alignments.Centered : Alignments.Left}>
            {title}
          </HeaderContent>
        </ModalHeader>
        <ModalContent>{children}</ModalContent>
      </ModalWrapper>
    </ModalContainer>
  )
}

type UseModalProps = [
  (_btnOriginRef?: React.RefObject<HTMLButtonElement>) => void,
  () => void,
]

export const useModal = (modalId: string): UseModalProps => {
  const { setShownModalId, setOriginBtnRef, hideModal } = useContext(ModalContext)

  const showModal = (btnOriginRef?: React.RefObject<HTMLButtonElement>) => {
    setShownModalId(modalId)
    if (btnOriginRef) {
      setOriginBtnRef(btnOriginRef)
    }
  }

  return [showModal, hideModal]
}

type Props = {
  children: ReactNode
  modals: ReactNode[]
}

export const ModalProvider = ({ children, modals }: Props) => {
  const [shownModalId, setShownModalId] = useState<string | null>(null)
  const [originBtnRef, setOriginBtnRef] =
    useState<React.RefObject<HTMLButtonElement> | null>(null)

  const hideModal = () => {
    setShownModalId(null)
    if (originBtnRef) {
      if (originBtnRef?.current?.nodeName === "BUTTON") {
        originBtnRef?.current?.focus()
      } else {
        const btn = originBtnRef?.current?.querySelector("button")
        btn?.focus()
      }
    }
    setOriginBtnRef(null)
  }

  const hide = () => {
    hideModal()
  }

  return (
    <ModalContext.Provider
      value={{ shownModalId, setShownModalId, originBtnRef, setOriginBtnRef, hideModal }}
    >
      {children}
      <Background className={shownModalId ? "" : "hidden"} onClick={hide} />
      {modals}
    </ModalContext.Provider>
  )
}
